import React, { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import { useWebviewMode } from '../../../hooks/useWebviewMode';
import CreatableSelect from 'react-select/creatable';
import cx from 'classnames';
import axios from 'axios';
import { useUser } from '@clerk/clerk-react';
import {
  API_NEWCLIENT,
  API_KEY,
  API_CLIENTS_NAME,
  API_GET_PLANS,
  API_GROUPS,
  AWS_BUCKET_CLIENT_LOGOS,
  S3_GET_PRESIGNED_URLS
} from '../../../utils/constants';
import { useSelector, useDispatch } from 'react-redux';
import { generateContent } from '../../../store/slices/generalState';
import { useHistory, useLocation } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import BusinessHours from '../AboutSection/BusinessHours/BusinessHours';
import { setSelectedClient } from '../../../store/slices/selectedClient';
import { clientsSelector, getClients } from '../../../store/slices/clients';

import { selectedClientSelector } from '../../../store/slices/selectedClient';
import Links from '../Links';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../../components/ui/dialog';

import {
  Home,
  Star,
  FileText,
  Image,
  LogOut,
  Menu,
  Facebook,
  Instagram,
  Check,
  X,
  CalendarDays,
  Building2,
  Search,
  Save,
  Phone,
  MapPin,
  Briefcase,
  Clock,
  MapPinned,
  Globe,
  LucideMail,
  Settings,
  ImageIcon
} from 'lucide-react';

import {
  Select as SelectPrimitive,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select';

import { useSidebar } from '../../../contexts/SidebarContext';
import { Spinner } from '../../../components/ui/spinner';
import TooltipInfo from '../TooltipInfo/tooltipInfo';
import { useIsMobile } from '../../../hooks/use-mobile';
import { useGooglePlaces } from '../../../hooks/use-google-places';
import { useLoadScript } from '@react-google-maps/api';

export const daysOrder = {
  Sunday: 1,
  Monday: 2,
  Tuesday: 3,
  Wednesday: 4,
  Thursday: 5,
  Friday: 6,
  Saturday: 7
};

const getHolidaysFromEnv = () => {
  const envHolidays = process.env.REACT_APP_HOLIDAYS_LIST;
  if (!envHolidays) return [];
  try {
    // Si la variable es un JSON, parsea, si es string separado por coma, divide
    if (envHolidays.trim().startsWith('[')) {
      return JSON.parse(envHolidays);
    }
    return envHolidays
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
};

const INDUSTRY_GROUPED_OPTIONS = [
  {
    label: 'Home Services',
    options: [
      { value: 'plumber', label: 'Plumber' },
      { value: 'electrician', label: 'Electrician' },
      { value: 'hvac', label: 'HVAC' },
      { value: 'general-contractor', label: 'General Contractor' },
      { value: 'roofer', label: 'Roofer' },
      { value: 'painter', label: 'Painter' },
      { value: 'landscaping', label: 'Landscaping / Lawn Care' },
      { value: 'cleaning-services', label: 'Cleaning Services' }
    ]
  },
  {
    label: 'Food & Hospitality',
    options: [
      { value: 'restaurant', label: 'Restaurant' },
      { value: 'cafe', label: 'Cafe / Coffee Shop' },
      { value: 'bakery', label: 'Bakery' }
    ]
  },
  {
    label: 'Health & Medical',
    options: [
      { value: 'dentist', label: 'Dentist' },
      { value: 'doctor', label: 'Doctor / Clinic' },
      { value: 'chiropractor', label: 'Chiropractor' }
    ]
  },
  {
    label: 'Personal Care & Wellness',
    options: [
      { value: 'hair-salon', label: 'Hair Salon' },
      { value: 'barber', label: 'Barber' },
      { value: 'spa', label: 'Spa / Massage' },
      { value: 'gym', label: 'Fitness / Gym' }
    ]
  },
  {
    label: 'Automotive',
    options: [
      { value: 'auto-repair', label: 'Auto Repair' },
      { value: 'car-wash', label: 'Car Wash' }
    ]
  },
  {
    label: 'Other',
    options: [{ value: 'other', label: 'Other' }]
  }
];

const INDUSTRY_OPTIONS = INDUSTRY_GROUPED_OPTIONS.flatMap(
  (group) => group.options
);

const BUSINESS_HOURS_PATTERN =
  /^((?:24|(?:2[0-3]|1[0-9]|[0-9])(?:\.(?:25|5|75))?)\/(?:24|(?:2[0-3]|1[0-9]|[0-9])(?:\.(?:25|5|75))?)|\/)$/;
const GOOGLE_MAPS_LIBRARIES = ['places'];

const formatServiceAreaPrediction = (prediction) => {
  const mainText = prediction?.structured_formatting?.main_text || '';
  const secondaryText =
    prediction?.structured_formatting?.secondary_text ||
    prediction?.description ||
    '';
  const state = secondaryText.match(/\b[A-Z]{2}\b/)?.[0];

  return state && mainText ? `${mainText}, ${state}` : null;
};

const UnifiedAboutContent = ({ callbackFromParent, defaultData = {} }) => {
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const { customerList } = useSelector(clientsSelector);
  const { user } = useUser();

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const selectedClientData = useSelector(selectedClientSelector);
  const isMobile = useIsMobile();
  const isWebviewMode = useWebviewMode();
  const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } =
    useLoadScript({
      googleMapsApiKey: process.env.REACT_APP_GCP_API_KEY || '',
      libraries: GOOGLE_MAPS_LIBRARIES
    });
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [gettingPlans, setGettingPlans] = useState(false);
  const [legacyIndustryMap, setLegacyIndustryMap] = useState({});
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');
  const [fullAccess, setFullAccess] = useState(false);

  // Logo management states
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [serviceAreaInput, setServiceAreaInput] = useState('');
  const {
    predictions: serviceAreaPredictions,
    loading: serviceAreaLoading,
    searchPlaces: searchServiceAreas,
    clearPredictions: clearServiceAreaPredictions
  } = useGooglePlaces(isGoogleMapsLoaded && !googleMapsLoadError);

  // Determine if user is superAdmin based on domain
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  // Check if user has "pro" plan in public metadata
  const hasProPlan = user?.publicMetadata?.plan === 'pro';

  const serviceAreaOptions = serviceAreaPredictions
    .map((prediction) => {
      const label = formatServiceAreaPrediction(prediction);
      return label
        ? { value: label, label, placeId: prediction.place_id }
        : null;
    })
    .filter(Boolean);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (serviceAreaInput.trim().length >= 3) {
        searchServiceAreas(serviceAreaInput.trim(), { types: ['geocode'] });
      } else {
        clearServiceAreaPredictions();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [serviceAreaInput, searchServiceAreas, clearServiceAreaPredictions]);

  const holidaysList = getHolidaysFromEnv();
  const isMobileRoute = location.pathname.startsWith('/mobile');
  const shouldShowMobileHeader = false; // Header now managed in Layout.js

  const initialValues = {
    clientName: '',
    timeZone: '',
    serviceArea: [],
    secondaryServiceArea: [],
    description: '',
    businessHours: {
      monday: '9/18',
      tuesday: '9/18',
      wednesday: '9/18',
      thursday: '9/18',
      friday: '9/18',
      saturday: '9/18',
      sunday: '9/18'
    },
    mergeFields: {
      '~Address': '',
      '~Phone': '',
      '~SecondaryPhone': '',
      '~Website': '',
      '~Email': ''
    },
    listingPhone: 'primary',
    industry: '',
    primaryService: [],
    additionalService: [],
    attributes: [],
    promotions: [],
    holidays: [],
    igTags: [],
    fbTags: [],
    negativeKeywords: [],
    galleries: {
      stockPhotos: { enabled: true },
      appLibrary: { enabled: false }
    },
    logoUrl: '',
    brand: {
      primaryColor: '#3B5998',
      secondaryColor: '#8392A5'
    },
    tier: '',
    automaticReviews: true,
    noAutomaticContent: false,
    singlePerson: false,
    clientFlags: [],
    tripAdvisorEmail: '',
    tripAdvisorPassword: ''
  };

  const userInfo = useSelector((state) => state.auth.userInfo);
  const timeZones = [
    { value: 1, label: 'Eastern Daylight Time - (GMT-4)' },
    { value: 2, label: 'Central Daylight Time - (GMT-5)' },
    { value: 3, label: 'Mountain Daylight Time - (GMT-6)' },
    { value: 4, label: 'Mountain Standard Time - (GMT-7)' },
    { value: 5, label: 'Pacific Daylight Time - (GMT-7)' },
    { value: 6, label: 'Alaska Daylight Time - (GMT-8)' },
    { value: 7, label: 'Hawaii-Aleutian Standard Time - (GMT-10)' }
  ];

  const normalizeText = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const timezoneAliases = {
    'america new york': 'Eastern Daylight Time - (GMT-4)',
    'america-chicago': 'Central Daylight Time - (GMT-5)',
    'america-denver': 'Mountain Daylight Time - (GMT-6)',
    'america-phoenix': 'Mountain Standard Time - (GMT-7)',
    'america los angeles': 'Pacific Daylight Time - (GMT-7)',
    'america-anchorage': 'Alaska Daylight Time - (GMT-8)',
    'pacific honolulu': 'Hawaii-Aleutian Standard Time - (GMT-10)',
    et: 'Eastern Daylight Time - (GMT-4)',
    ct: 'Central Daylight Time - (GMT-5)',
    mt: 'Mountain Daylight Time - (GMT-6)',
    mdt: 'Mountain Daylight Time - (GMT-6)',
    mst: 'Mountain Standard Time - (GMT-7)',
    pt: 'Pacific Daylight Time - (GMT-7)',
    pdt: 'Pacific Daylight Time - (GMT-7)',
    akt: 'Alaska Daylight Time - (GMT-8)',
    ht: 'Hawaii-Aleutian Standard Time - (GMT-10)'
  };

  const resolveTimeZoneOption = (timeZoneValue) => {
    if (timeZoneValue === null || timeZoneValue === undefined) {
      return null;
    }

    const normalized = normalizeText(timeZoneValue);

    const directMatch = timeZones.find(
      (option) =>
        String(option.value) === String(timeZoneValue) ||
        normalizeText(option.label) === normalized
    );

    if (directMatch) {
      return directMatch;
    }

    const aliasLabel = timezoneAliases[normalized];
    if (aliasLabel) {
      const aliasMatch = timeZones.find(
        (option) => option.label === aliasLabel
      );
      if (aliasMatch) {
        return aliasMatch;
      }
    }

    const timezoneKeywordMatch = (() => {
      if (normalized.includes('eastern')) {
        return timeZones.find((option) => option.label.includes('Eastern'));
      }
      if (normalized.includes('central')) {
        return timeZones.find((option) => option.label.includes('Central'));
      }
      if (normalized.includes('mountain standard')) {
        return timeZones.find((option) =>
          option.label.includes('Mountain Standard')
        );
      }
      if (normalized.includes('mountain')) {
        return timeZones.find((option) =>
          option.label.includes('Mountain Daylight')
        );
      }
      if (normalized.includes('pacific')) {
        return timeZones.find((option) => option.label.includes('Pacific'));
      }
      if (normalized.includes('alaska')) {
        return timeZones.find((option) => option.label.includes('Alaska'));
      }
      if (normalized.includes('hawaii') || normalized.includes('honolulu')) {
        return timeZones.find((option) => option.label.includes('Hawaii'));
      }
      return null;
    })();

    if (timezoneKeywordMatch) {
      return timezoneKeywordMatch;
    }

    return null;
  };

  const resolveIndustryOption = (industryValue) => {
    if (
      industryValue === null ||
      industryValue === undefined ||
      industryValue === ''
    ) {
      return null;
    }

    const normalized = normalizeText(industryValue);

    const exactMatch = INDUSTRY_OPTIONS.find(
      (option) =>
        String(option?.value) === String(industryValue) ||
        normalizeText(option?.label) === normalized
    );

    if (exactMatch) {
      return exactMatch;
    }

    const compactNormalized = normalized.replace(/[^a-z0-9]/g, '');
    const fuzzyMatch = INDUSTRY_OPTIONS.find((option) => {
      const optionLabelNormalized = normalizeText(option?.label);
      const optionCompact = optionLabelNormalized.replace(/[^a-z0-9]/g, '');
      return (
        optionCompact.includes(compactNormalized) ||
        compactNormalized.includes(optionCompact)
      );
    });

    if (fuzzyMatch) {
      return fuzzyMatch;
    }

    const legacyIndustryLabel = legacyIndustryMap[String(industryValue)];
    if (legacyIndustryLabel) {
      const normalizedLegacyLabel = normalizeText(legacyIndustryLabel);
      const legacyMappedMatch = INDUSTRY_OPTIONS.find(
        (option) => normalizeText(option?.label) === normalizedLegacyLabel
      );

      if (legacyMappedMatch) {
        return legacyMappedMatch;
      }

      return {
        value: industryValue,
        label: legacyIndustryLabel
      };
    }

    return {
      value: industryValue,
      label: String(industryValue)
    };
  };

  const attributeList = [
    { value: 1, label: 'Curbside Pickup' },
    { value: 2, label: 'Deliver' },
    { value: 3, label: 'Dine-in' },
    { value: 4, label: 'Drive through' },
    { value: 5, label: 'No-contact delivery' },
    { value: 6, label: 'Outdoor seating' },
    { value: 7, label: 'Takeout' },
    { value: 8, label: 'Assistive hearing loop' },
    { value: 9, label: 'Beach wheelchairs' },
    { value: 10, label: 'Mobility scooter rental' },
    { value: 11, label: 'Passenger loading area' },
    { value: 12, label: 'Wheelchair-accessible elevator' },
    { value: 13, label: 'Wheelchair-accessible entrance' },
    { value: 14, label: 'Wheelchair-accessible parking' },
    { value: 15, label: 'Wheelchair-accessible restroom' },
    { value: 16, label: 'Wheelchair-accessible seating' },
    { value: 17, label: 'Adults only' },
    { value: 18, label: 'Family-friendly' },
    { value: 19, label: 'Kid-friendly' },
    { value: 20, label: 'LGBTQ+ friendly' },
    { value: 21, label: 'Transgender safespace' },
    { value: 22, label: 'Accepting new patients' },
    { value: 23, label: 'Accepts reservations' },
    { value: 24, label: 'Appointment required' },
    { value: 25, label: 'Membership required' },
    { value: 26, label: 'Online events calendar' },
    { value: 27, label: 'Reservations recommended' },
    { value: 28, label: 'Reservations required' },
    { value: 29, label: 'Beach access' },
    { value: 30, label: 'Bicycles for rent' },
    { value: 31, label: 'Boats for rent' },
    { value: 32, label: 'Game room' },
    { value: 33, label: 'Horseback riding' },
    { value: 34, label: 'Jogging track' },
    { value: 35, label: 'Nightclub' },
    { value: 36, label: 'Scuba diving' },
    { value: 37, label: 'Snorkeling' },
    { value: 38, label: 'Tennis' },
    { value: 39, label: 'Water skiing' },
    { value: 40, label: 'Identifies as black owned' },
    { value: 41, label: 'Identifies as veteran-led' },
    { value: 42, label: 'Identifies as women-led' },
    { value: 43, label: 'Cash Only' },
    { value: 44, label: 'Checks' },
    { value: 45, label: 'Debit Cards' },
    { value: 46, label: 'NFC Mobile Payments' },
    { value: 47, label: 'American Express' },
    { value: 48, label: 'Master Card' },
    { value: 49, label: 'Visa' },
    { value: 50, label: 'Discover' },
    { value: 51, label: 'Diners club' }
  ];

  const PRIMARY_COLOR = '#3B5998';
  const ERROR_COLOR = '#dc3545';
  const BORDER_COLOR = '#e2e8f0';
  const SELECT_HEIGHT = '56px';

  const getBaseSelectStyles = (hasError) => ({
    control: (provided, state) => ({
      ...provided,
      minHeight: SELECT_HEIGHT,
      height: SELECT_HEIGHT,
      borderRadius: '0.75rem',
      borderWidth: '2px',
      borderColor: hasError
        ? ERROR_COLOR
        : state.isFocused
        ? PRIMARY_COLOR
        : BORDER_COLOR,
      boxShadow: 'none',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        borderColor: hasError ? ERROR_COLOR : PRIMARY_COLOR
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 1rem',
      minHeight: SELECT_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem'
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#0f172a'
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#94a3b8'
    }),
    input: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      margin: 0,
      padding: 0
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? PRIMARY_COLOR : '#94a3b8',
      paddingRight: '1rem'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#94a3b8',
      paddingRight: '1rem',
      '&:hover': {
        color: '#475569'
      }
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: 4,
      borderRadius: '0.75rem',
      border: `1px solid ${BORDER_COLOR}`,
      boxShadow: '0 20px 45px rgba(15, 23, 42, 0.12)',
      overflow: 'hidden'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '1rem',
      padding: '0.75rem 1rem',
      backgroundColor: state.isSelected
        ? PRIMARY_COLOR
        : state.isFocused
        ? 'rgba(59, 89, 152, 0.1)'
        : 'transparent',
      color: state.isSelected ? '#ffffff' : '#0f172a',
      cursor: 'pointer'
    })
  });

  const customMultiSelectorStyle = {
    control: (provided, state) => ({
      ...provided,
      minHeight: SELECT_HEIGHT,
      borderRadius: '0.75rem',
      borderWidth: '2px',
      borderColor: state?.isFocused ? PRIMARY_COLOR : BORDER_COLOR,
      boxShadow: 'none',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      padding: '0 4px',
      '&:hover': {
        borderColor: PRIMARY_COLOR
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0.5rem 1rem',
      minHeight: SELECT_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap'
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '1.125rem',
      color: '#94a3b8'
    }),
    input: (provided) => ({
      ...provided,
      fontSize: '1.125rem',
      margin: 0,
      padding: 0
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state?.isFocused ? PRIMARY_COLOR : '#94a3b8',
      paddingRight: '1rem'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#94a3b8',
      paddingRight: '1rem',
      '&:hover': {
        color: '#475569'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: 4,
      borderRadius: '0.75rem',
      border: `1px solid ${BORDER_COLOR}`,
      boxShadow: '0 20px 45px rgba(15, 23, 42, 0.12)',
      overflow: 'hidden'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: 200
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '1rem',
      padding: '0.75rem 1rem',
      backgroundColor: state?.isSelected
        ? PRIMARY_COLOR
        : state?.isFocused
        ? 'rgba(59, 89, 152, 0.1)'
        : 'transparent',
      color: state?.isSelected ? '#ffffff' : '#0f172a',
      cursor: 'pointer'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#f4f4f4',
      border: `1px solid #f4f4f4`,
      borderRadius: '9999px',
      color: '#0f172a',
      fontWeight: 500,
      fontSize: '1rem',
      margin: '2px 4px',
      padding: '0 0.25rem',
      display: 'flex',
      alignItems: 'center',
      boxShadow: 'none',
      minHeight: '32px',
      height: '32px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#0f172a',
      fontWeight: 700,
      fontSize: '0.75rem',
      padding: 0,
      marginRight: 4
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#64748b',
      borderRadius: '9999px',
      marginLeft: 2,
      fontWeight: 700,
      fontSize: '0.75rem',
      padding: '0 2px',
      transition: 'background 0.15s',
      '&:hover': {
        backgroundColor: '#e2e8f0',
        color: '#0f172a'
      }
    })
  };

  const validationSchema = yup.object().shape({
    clientName: yup.string().required('Client name is required'),
    timeZone: yup.string().required('Time zone is required'),
    serviceArea: yup
      .array()
      .min(1, 'Primary Service Area is required')
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚ][a-zA-Z0-9áéíóúÁÉÍÓÚ\s',.~-]*$/,
            'A service area has an invalid format'
          )
      ),
    secondaryServiceArea: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚ][a-zA-Z0-9áéíóúÁÉÍÓÚ\s',.~-]*$/,
            'A secondary service area has an invalid format'
          )
      ),
    description: yup.string(),
    businessHours: yup.object().shape({
      monday: yup
        .string()
        .matches(
          BUSINESS_HOURS_PATTERN,
          'Invalid format hour should be a number between 0-24'
        ),
      tuesday: yup
        .string()
        .matches(
          BUSINESS_HOURS_PATTERN,
          'Invalid format hour should be a number between 0-24'
        ),
      wednesday: yup
        .string()
        .matches(
          BUSINESS_HOURS_PATTERN,
          'Invalid format hour should be a number between 0-24'
        ),
      thursday: yup
        .string()
        .matches(
          BUSINESS_HOURS_PATTERN,
          'Invalid format hour should be a number between 0-24'
        ),
      friday: yup
        .string()
        .matches(
          BUSINESS_HOURS_PATTERN,
          'Invalid format hour should be a number between 0-24'
        ),
      saturday: yup
        .string()
        .matches(
          BUSINESS_HOURS_PATTERN,
          'Invalid format hour should be a number between 0-24'
        ),
      sunday: yup
        .string()
        .matches(
          BUSINESS_HOURS_PATTERN,
          'Invalid format hour should be a number between 0-24'
        )
    }),
    mergeFields: yup.object().shape({
      '~Address': yup
        .string()
        .nullable()
        .transform((v) => v || ''),
      '~Phone': yup
        .string()
        .nullable()
        .transform((v) => v || '')
        .required('Phone is required'),
      '~SecondaryPhone': yup
        .string()
        .nullable()
        .transform((v) => v || ''),
      '~Website': yup
        .string()
        .nullable()
        .transform((v) => v || ''),
      '~Email': yup
        .string()
        .nullable()
        .transform((v) => v || '')
    }),
    industry: yup.string().required('Industry is required'),
    primaryService: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'Primary Service contains unsupported characters. Remove symbols like ® or ™.'
          )
      ),
    additionalService: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'An additional service has an invalid format'
          )
      ),
    attributes: yup.array().of(yup.string()),
    promotions: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A promotion has an invalid format'
          )
      ),
    holidays: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'Invalid format'
          )
      ),
    igTags: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A tag has an invalid format'
          )
      ),
    fbTags: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A tag has an invalid format'
          )
      ),
    negativeKeywords: yup.array().of(yup.string()),
    galleries: yup.object().shape({
      stockPhotos: yup.object().shape({ enabled: yup.boolean() }),
      appLibrary: yup.object().shape({ enabled: yup.boolean() })
    }),
    logoUrl: yup.string().nullable().optional(),
    brand: yup.object().shape({
      primaryColor: yup.string(),
      secondaryColor: yup.string()
    }),
    tier: yup.string(),
    automaticReviews: yup.boolean(),
    noAutomaticContent: yup.boolean(),
    singlePerson: yup.boolean(),
    clientFlags: yup.array().of(yup.string()),
    tripAdvisorEmail: yup.string().email('Invalid email format'),
    tripAdvisorPassword: yup.string()
  });

  const editing = Object.keys(defaultData).length > 0;

  const SingleValue = ({ children, ...props }) => (
    <components.SingleValue {...props} className="selected-option">
      {children}
      <div className="selected-logo">
        <Connected />
      </div>
    </components.SingleValue>
  );

  const Option = (props) => (
    <components.Option {...props}>
      <div className="country-option">
        {props.data.label}
        <Disconnected />
      </div>
    </components.Option>
  );

  const Connected = () => (
    <img src="../assets/img/Connected.png" alt="connected" />
  );

  const Disconnected = () => (
    <img src="../assets/img/Disconnected.png" alt="disconnected" />
  );

  // Fetch plans and content strategies
  useEffect(() => {
    const getPlans = async () => {
      setGettingPlans(true);
      try {
        const response = await axios.get(`${API_GET_PLANS}`);
        const plans = response.data;
        const order = ['Starter Plan', 'Plus Plan', 'Pro Plan', 'Elite Plan'];
        const formattedPlans = plans.map((plan) => ({
          ...plan,
          value: plan.PK,
          label: plan.SK
        }));
        const sortedPlans = formattedPlans.sort(
          (a, b) => order.indexOf(a.label) - order.indexOf(b.label)
        );
        setPlans(sortedPlans);
      } catch (e) {
        console.error(e);
      } finally {
        setGettingPlans(false);
      }
    };

    if (plans.length === 0 && !gettingPlans) {
      getPlans();
    }
  }, []);

  useEffect(() => {
    const loadLegacyIndustryMap = async () => {
      if (!userInfo?.accessToken) return;

      try {
        const response = await axios.get(API_GROUPS, {
          headers: { Authorization: `bearer ${userInfo.accessToken}` }
        });

        const groups = response?.data?.groups || [];
        const mapped = groups.reduce((acc, group) => {
          if (group?.id !== undefined && group?.groupName) {
            acc[String(group.id)] = group.groupName;
          }
          return acc;
        }, {});

        setLegacyIndustryMap(mapped);
      } catch (e) {
        console.error('API_GROUPS legacy mapping error:', e);
      }
    };

    loadLegacyIndustryMap();
  }, [userInfo?.accessToken]);

  // Initialize logo preview from existing client data
  useEffect(() => {
    // Use logoUrl directly from the database (which includes timestamp if logo was uploaded)
    if (defaultData?.logoUrl) {
      setLogoPreview(defaultData.logoUrl);
    } else {
      setLogoPreview(null);
    }
    setLoadingLogo(false);
  }, [defaultData?.logoUrl]);

  // Logo upload function to S3 with client ID
  const uploadLogoToS3WithClientId = async (file, clientId) => {
    const fileType = file.type;
    const fileExtension = file.name.split('.').pop();
    // Use clientId as prefix: clientId/logo.ext
    const fileName = `${clientId}/logo.${fileExtension}`;

    try {
      // 1. Get presigned URL from API Gateway
      const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucketName: AWS_BUCKET_CLIENT_LOGOS,
          files: [{ fileName, fileType }]
        })
      });

      if (!presignedResponse.ok) {
        throw new Error('Failed to get presigned URL');
      }

      const { urls } = await presignedResponse.json();
      const presignedUrl = urls?.[0]?.url;

      if (!presignedUrl) {
        throw new Error('No presigned URL returned');
      }

      // 2. Upload file directly to S3
      const uploadResult = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': fileType
        },
        body: file
      });

      if (!uploadResult.ok) {
        throw new Error('File upload to S3 failed');
      }

      // 3. Return the uploaded logo URL with timestamp to force cache refresh
      const uploadedFileUrl = `https://${AWS_BUCKET_CLIENT_LOGOS}.s3.amazonaws.com/${fileName}?v=${Date.now()}`;
      return uploadedFileUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Save file to upload later
      setLogoFile(file);

      // Show local preview
      const reader = new window.FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setLogoFile(null);
  };

  const handlePut = async (data = {}) => {
    console.log('[UnifiedAboutContent] Save started', {
      clientName: data?.clientName,
      clientId: defaultData?.id || null,
      hasLogoFile: Boolean(logoFile)
    });
    setLoading(true);
    let newClientName = data.clientName.replace(/\s+/g, ' ');
    newClientName = newClientName.replace(/^\s+|\s+$/g, '');
    newClientName = newClientName.replace(/'/g, "'");

    // Upload logo FIRST if there's a new file, before saving client data
    let uploadedLogoUrl = null;
    if (logoFile && defaultData?.id) {
      try {
        setUploadingLogo(true);
        uploadedLogoUrl = await uploadLogoToS3WithClientId(
          logoFile,
          defaultData.id
        );
      } catch (logoError) {
        console.error('Error uploading logo:', logoError);
        const uploadErrorMessage =
          logoError?.message || 'Logo upload failed. Please try again.';
        setErrorDetail(uploadErrorMessage);
        setErrorModalOpen(true);
        setUploadingLogo(false);
        setLoading(false);
        return; // Stop execution if logo upload fails
      } finally {
        setUploadingLogo(false);
      }
    }

    // Aseguramos que los valores de secondary phone y listingPhone estén presentes
    const clientData = {
      ...data,
      id: defaultData?.id,
      clientName: newClientName,
      mergeFields: {
        ...data.mergeFields,
        '~SecondaryPhone': data.mergeFields?.['~SecondaryPhone'] || ''
      },
      listingPhone: data.listingPhone || 'primary',
      noAutomaticContent: Boolean(data.noAutomaticContent),
      // Include the uploaded logo URL if available
      ...(uploadedLogoUrl && { logoUrl: uploadedLogoUrl })
    };

    try {
      const response = await axios({
        method: 'post',
        url: API_NEWCLIENT,
        data: clientData
      });

      if (response.status === 200) {
        console.log('[UnifiedAboutContent] Save success', {
          clientName: clientData?.clientName,
          clientId: clientData?.id || null
        });
        setErrorDetail('');
        setSuccessModalOpen(true);

        // Clear the logo file state after successful save
        if (uploadedLogoUrl) {
          setLogoFile(null);
        }

        callbackFromParent(clientData);

        if (clientData?.id) {
          const clData = await axios.get(
            `${API_CLIENTS_NAME}${clientData.clientName}`,
            {
              headers: {
                'X-API-KEY': API_KEY,
                Authorization: `bearer ${userInfo.accessToken}`
              }
            }
          );
          // Stay on /client page, don't redirect
          if (clData.data) {
            dispatch(
              setSelectedClient({
                ...clData.data,
                noAutomaticContent:
                  clData.data?.noAutomaticContent ??
                  clientData?.noAutomaticContent ??
                  false
              })
            );
          }
        } else {
          const clData = await axios.get(
            `${API_CLIENTS_NAME}${clientData.clientName}`,
            {
              headers: {
                'X-API-KEY': API_KEY,
                Authorization: `bearer ${userInfo.accessToken}`
              }
            }
          );
          let newUrl = clientData.clientName
            .replace(/\s+/g, '%20')
            .replace(/\//g, '%2F')
            .replace(/'/g, '%1D')
            .replace(/'/g, '%1E');
          history.push('/calendar');
          if (clData.data) {
            dispatch(
              setSelectedClient({
                ...clData.data,
                noAutomaticContent:
                  clData.data?.noAutomaticContent ??
                  clientData?.noAutomaticContent ??
                  false
              })
            );
            dispatch(generateContent());
          }
        }
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Unknown error while saving the client';
      console.error('[UnifiedAboutContent] Save failed', {
        message: apiMessage,
        status: error?.response?.status,
        data: error?.response?.data,
        clientName: clientData?.clientName,
        clientId: clientData?.id || null
      });
      setErrorDetail(apiMessage);
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle change in client search select
  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;

    try {
      // Fetch complete client data from API to ensure all attributes are present
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        // Update selected client in Redux with complete data including socialnetworks
        dispatch(
          setSelectedClient({
            ...response.data,
            noAutomaticContent:
              response.data?.noAutomaticContent ??
              selectedOption?.noAutomaticContent ??
              false
          })
        );
      } else {
        // Fallback to using the client from customerList if API call returns no data
        dispatch(
          setSelectedClient({
            ...selectedOption,
            noAutomaticContent:
              selectedOption?.noAutomaticContent ??
              selectedClientData?.selectedClient?.noAutomaticContent ??
              false
          })
        );
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      // Fallback to using the client from customerList if API call fails
      dispatch(
        setSelectedClient({
          ...selectedOption,
          noAutomaticContent:
            selectedOption?.noAutomaticContent ??
            selectedClientData?.selectedClient?.noAutomaticContent ??
            false
        })
      );
    }
  };

  // Set label and value for search
  const getCustomerList = () => {
    if (!customerList?.length) {
      dispatch(getClients());
    }

    return customerList.map((customer) => ({
      ...customer,
      label: customer.clientName,
      value: customer.clientName
    }));
  };

  return (
    <Formik
      onSubmit={handlePut}
      enableReinitialize
      validateOnBlur={true}
      initialValues={
        editing
          ? {
              ...defaultData,
              ...(!defaultData.businessHours?.monday
                ? { businessHours: initialValues.businessHours }
                : {}),
              ...(!defaultData?.brand?.primaryColor
                ? { brand: initialValues.brand }
                : {}),
              mergeFields: {
                ...initialValues.mergeFields,
                ...defaultData.mergeFields
              },
              listingPhone:
                defaultData.listingPhone ||
                (defaultData.mergeFields?.['~SecondaryPhone'] &&
                defaultData.listingPhone === 'secondary'
                  ? 'secondary'
                  : 'primary')
            }
          : initialValues
      }
      validationSchema={validationSchema}>
      {(props) => {
        const {
          submitForm,
          validateForm,
          setFieldValue,
          setFieldTouched,
          values,
          handleChange,
          handleBlur,
          errors,
          touched,
          setTouched
        } = props;

        const getFirstErrorMessage = (errorValue) => {
          if (!errorValue) return null;
          if (typeof errorValue === 'string') return errorValue;
          if (Array.isArray(errorValue)) {
            for (const nestedError of errorValue) {
              const extracted = getFirstErrorMessage(nestedError);
              if (extracted) return extracted;
            }
            return null;
          }
          if (typeof errorValue === 'object') {
            for (const nestedError of Object.values(errorValue)) {
              const extracted = getFirstErrorMessage(nestedError);
              if (extracted) return extracted;
            }
          }
          return null;
        };

        const primaryServicePattern =
          /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/;
        const isPrimaryServiceValueInvalid = (value) =>
          typeof value === 'string' && !primaryServicePattern.test(value);

        const markErrorsAsTouched = (validationErrors) => {
          const touchedFields = {};

          Object.keys(validationErrors || {}).forEach((key) => {
            if (
              typeof validationErrors[key] === 'object' &&
              !Array.isArray(validationErrors[key])
            ) {
              touchedFields[key] = {};
              Object.keys(validationErrors[key]).forEach((subKey) => {
                touchedFields[key][subKey] = true;
              });
            } else {
              touchedFields[key] = true;
            }
          });

          setTouched(touchedFields);
        };

        const handleSaveClick = async (event) => {
          if (event?.preventDefault) {
            event.preventDefault();
          }

          console.log('[UnifiedAboutContent] Save button clicked', {
            clientName: values?.clientName,
            clientId: defaultData?.id || null,
            hasProPlan,
            loading,
            uploadingLogo
          });

          try {
            const validationErrors = await validateForm();
            const hasValidationErrors =
              Object.keys(validationErrors || {}).length > 0;

            if (hasValidationErrors) {
              markErrorsAsTouched(validationErrors);

              const firstErrorPath = Object.keys(validationErrors)[0];
              const firstErrorValue = validationErrors[firstErrorPath];
              const firstErrorMessage =
                getFirstErrorMessage(firstErrorValue) ||
                'Please check the highlighted fields.';

              const errorMessage = `Validation error in "${firstErrorPath}": ${firstErrorMessage}`;
              console.warn('[UnifiedAboutContent] Save blocked by validation', {
                validationErrors,
                firstErrorPath,
                firstErrorMessage
              });

              setErrorDetail(errorMessage);
              setErrorModalOpen(true);
              return;
            }

            await submitForm();
          } catch (unexpectedError) {
            const message =
              unexpectedError?.message ||
              'Unexpected error before sending save request.';
            console.error(
              '[UnifiedAboutContent] Unexpected save handler error',
              unexpectedError
            );
            setErrorDetail(message);
            setErrorModalOpen(true);
          }
        };

        return (
          <>
            {/* Content Area */}
            <Form
              autoComplete="off"
              className="px-4 py-4 sm:p-6 lg:p-10 space-y-4 sm:space-y-8 needs-validation overflow-x-hidden"
              noValidate>
              {!isMobile && (
                <div className="sm:flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 mb-2 min-w-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                        Welcome back,{' '}
                        {selectedClientData
                          ? selectedClientData.selectedClient.clientName
                          : ''}
                      </h1>
                      {hasProPlan && (
                        <TooltipInfo
                          accountOwner={
                            selectedClientData.selectedClient?.accountOwner
                          }
                          accountStatus={
                            selectedClientData.selectedClient?.accountStatus
                          }
                          clientFlags={
                            selectedClientData.selectedClient?.clientFlags
                          }
                        />
                      )}
                    </div>
                    <p className="text-sm sm:text-base text-muted-foreground mt-1">
                      Update your business info, connected platforms, and
                      public-facing details
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
                    {isSuperAdmin && !isMobile && !isWebviewMode && (
                      <div className="relative w-full sm:w-72">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                        <Select
                          placeholder="Search"
                          onChange={(client) => handleChangeClient(client)}
                          options={getCustomerList()}
                          hideSelectedOptions={true}
                          controlShouldRenderValue={false}
                          components={{
                            DropdownIndicator: () => null,
                            IndicatorSeparator: () => null
                          }}
                          openMenuOnClick={false}
                          openMenuOnFocus={false}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (provided, state) => ({
                              ...provided,
                              borderRadius: '0.75rem',
                              borderWidth: '1px',
                              borderColor: state.isFocused
                                ? 'hsl(var(--primary))'
                                : 'hsl(var(--border))',
                              boxShadow: state.isFocused
                                ? '0 0 0 1px hsl(var(--primary))'
                                : 'none',
                              backgroundColor: '#ffffff',
                              paddingTop: '10px',
                              paddingBottom: '9px',
                              paddingLeft: '2.5rem',
                              fontSize: '1rem',
                              '&:hover': {
                                borderColor: 'hsl(var(--primary))'
                              }
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: 'hsl(var(--muted-foreground))',
                              fontSize: '1rem'
                            }),
                            input: (provided) => ({
                              ...provided,
                              fontSize: '1rem',
                              color: 'hsl(var(--foreground))'
                            }),
                            menu: (provided) => ({
                              ...provided,
                              borderRadius: '0.75rem',
                              overflow: 'hidden',
                              boxShadow:
                                '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isFocused
                                ? '#f1f5f9'
                                : 'white',
                              color: state.isFocused ? '#1e293b' : '#64748b',
                              cursor: 'pointer',
                              padding: '12px 16px',
                              fontSize: '1rem'
                            })
                          }}
                        />
                      </div>
                    )}
                    {hasProPlan && !isMobile && (
                      <Button
                        size="lg"
                        type="button"
                        disabled={loading || uploadingLogo}
                        onClick={handleSaveClick}
                        style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 sm:h-14 w-full sm:w-auto px-6 sm:px-8 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3">
                        <Save className="h-5 w-5" />
                        {uploadingLogo ? (
                          'Uploading Logo...'
                        ) : loading ? (
                          <Spinner className="h-5 w-5" />
                        ) : (
                          'Save Profile'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              <Links selectedClient={defaultData} userInfo={userInfo} />

              <Card className="shadow-md rounded-xl sm:rounded-2xl border-0 overflow-hidden">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="h-9 w-9 sm:h-12 sm:w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 className="h-4 w-4 sm:h-6 sm:w-6 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base sm:text-xl lg:text-2xl font-bold break-words">
                        Business Information
                      </CardTitle>
                      <CardDescription className="sm:block text-sm sm:text-base mt-1">
                        This info appears on your listings and directory
                        profiles
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6 sm:space-y-8 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  {/* Business Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label
                        htmlFor="businessName"
                        className="text-sm sm:text-base font-semibold flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                        Business Name
                      </Label>
                      <p className="text-sm text-muted-foreground -mt-1">
                        The official name of your business as it appears on your
                        listings
                      </p>
                      <Input
                        id="clientName"
                        type="text"
                        required
                        placeholder="Enter business name"
                        value={values?.clientName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!hasProPlan}
                        className={`h-12 sm:h-14 text-base sm:text-lg rounded-xl border-2 px-4 ${
                          touched?.clientName && errors?.clientName
                            ? 'border-red-500 focus:border-red-500'
                            : 'border-border focus:border-primary'
                        }`}
                      />
                      {touched?.clientName && errors?.clientName && (
                        <p className="text-red-500 text-sm">
                          {errors?.clientName}
                        </p>
                      )}
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="businessName"
                        className="text-sm sm:text-base font-semibold flex items-center gap-2">
                        <LucideMail className="h-5 w-5 text-muted-foreground" />
                        Email
                      </Label>
                      <p className="text-sm text-muted-foreground -mt-1">
                        Public contact email shown on your listings
                      </p>

                      <Input
                        id="mergeFields.~Email"
                        type="text"
                        onChange={handleChange}
                        value={values?.mergeFields['~Email'] || ''}
                        placeholder="Enter Email"
                        onBlur={handleBlur}
                        disabled={!hasProPlan}
                        className={`h-12 sm:h-14 text-base sm:text-lg rounded-xl border-2 px-4 ${
                          touched?.mergeFields?.['~Email'] &&
                          errors?.mergeFields?.['~Email']
                            ? 'border-red-500 focus:border-red-500'
                            : 'border-border focus:border-primary'
                        }`}
                      />
                      {touched?.mergeFields?.['~Email'] &&
                        errors?.mergeFields?.['~Email'] && (
                          <p className="text-red-500 text-sm">
                            {errors?.mergeFields?.['~Email']}
                          </p>
                        )}
                    </div>
                  </div>
                  {/* Business Logo */}
                  <div className="space-y-3">
                    <Label className="text-sm sm:text-base font-semibold flex items-center gap-2">
                      <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      Business Logo
                    </Label>
                    <p className="text-sm text-muted-foreground -mt-1">
                      Upload or update the company logo to display across
                      listings
                    </p>
                    <div className="flex items-start gap-4 sm:gap-6">
                      {loadingLogo ? (
                        <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl border-2 border-border bg-muted flex items-center justify-center">
                          <Spinner className="h-6 w-6" />
                        </div>
                      ) : logoPreview ? (
                        <div className="relative shrink-0">
                          <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl border-2 border-border overflow-hidden bg-muted flex items-center justify-center">
                            <img
                              src={logoPreview}
                              alt="Business logo"
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={removeLogo}
                            disabled={!hasProPlan}
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-md hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            <svg
                              className="h-3.5 w-3.5"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/50 flex items-center justify-center">
                          <ImageIcon className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground/50" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <label
                          htmlFor="logo-upload-edit"
                          className="gap-2 h-10 sm:h-12 px-4 sm:px-6 rounded-xl text-sm sm:text-base border border-border bg-background transition-colors flex items-center inline-flex cursor-pointer hover:bg-muted"
                          style={{
                            pointerEvents: hasProPlan ? 'auto' : 'none',
                            opacity: hasProPlan ? 1 : 0.5
                          }}>
                          <svg
                            className="h-5 w-5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                          {logoPreview ? 'Change Logo' : 'Upload Logo'}
                        </label>
                        <input
                          id="logo-upload-edit"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoUpload}
                          disabled={!hasProPlan}
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          PNG, JPG or SVG. Max 2MB. Will be uploaded when you
                          save.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label
                        htmlFor="phone"
                        className="text-sm sm:text-base font-semibold flex items-center gap-2">
                        <Phone className="h-5 w-5 text-muted-foreground" />
                        Phone Number
                      </Label>
                      <p className="text-sm text-muted-foreground -mt-1">
                        Your main business phone number
                      </p>
                      <Input
                        id="mergeFields.~Phone"
                        type="text"
                        onChange={handleChange}
                        value={values?.mergeFields['~Phone'] || ''}
                        className={`h-12 sm:h-14 text-base sm:text-lg rounded-xl border-2 px-4 ${
                          touched?.mergeFields?.['~Phone'] &&
                          errors?.mergeFields?.['~Phone']
                            ? 'border-red-500 focus:border-red-500'
                            : 'border-border focus:border-primary'
                        }`}
                        placeholder="Enter Phone"
                        onBlur={handleBlur}
                        name="mergeFields.~Phone"
                        disabled={!hasProPlan}
                      />
                      {touched?.mergeFields?.['~Phone'] &&
                        errors?.mergeFields?.['~Phone'] && (
                          <p className="text-red-500 text-sm">
                            {errors?.mergeFields?.['~Phone']}
                          </p>
                        )}
                      <div className="flex items-center space-x-3 pt-1">
                        <input
                          type="radio"
                          id="primaryListing"
                          name="listingPhone"
                          value="primary"
                          checked={values.listingPhone === 'primary'}
                          onChange={handleChange}
                          disabled={!hasProPlan}
                          className="h-5 w-5 rounded-full border-2 border-primary focus:ring-primary"
                        />
                        <label
                          htmlFor="primaryListing"
                          className="text-sm sm:text-base text-muted-foreground cursor-pointer">
                          Use this as my primary listing number
                        </label>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="phone"
                        className="text-sm sm:text-base font-semibold flex items-center gap-2">
                        <Phone className="h-5 w-5 text-muted-foreground" />
                        Secondary Phone
                      </Label>
                      <p className="text-sm text-muted-foreground -mt-1">
                        An alternate number customers can reach you at
                      </p>
                      <Input
                        id="mergeFields.~SecondaryPhone"
                        name="mergeFields.~SecondaryPhone"
                        type="text"
                        onChange={handleChange}
                        value={values?.mergeFields['~SecondaryPhone'] || ''}
                        className={`h-12 sm:h-14 text-base sm:text-lg rounded-xl border-2 focus:border-primary px-4 `}
                        placeholder="Enter Secondary Phone"
                        onBlur={handleBlur}
                        disabled={!hasProPlan}
                      />
                      <div className="flex items-center space-x-3 pt-1">
                        <input
                          type="radio"
                          id="secondaryListing"
                          name="listingPhone"
                          value="secondary"
                          checked={values.listingPhone === 'secondary'}
                          onChange={handleChange}
                          disabled={!hasProPlan}
                          className="h-5 w-5 rounded-full border-2 border-primary focus:ring-primary"
                        />
                        <label
                          htmlFor="secondaryListing"
                          className="text-sm sm:text-base text-muted-foreground cursor-pointer">
                          Use this as my primary listing number
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label
                        htmlFor="industry"
                        className="text-sm sm:text-base font-semibold flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-muted-foreground" />
                        Industry
                      </Label>
                      <p className="text-sm text-muted-foreground -mt-1">
                        Select the category that best describes your business
                      </p>
                      <SelectPrimitive
                        value={values?.industry || ''}
                        onValueChange={(selectedValue) =>
                          setFieldValue('industry', selectedValue)
                        }
                        disabled={!hasProPlan}>
                        <SelectTrigger
                          id="industry"
                          className={`h-12 sm:h-14 text-base sm:text-lg rounded-xl border-2 px-4 bg-background w-full ${
                            touched?.industry && errors?.industry
                              ? 'border-red-500 focus:border-red-500'
                              : 'border-border focus:border-primary'
                          }`}>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent className="bg-card z-50 max-h-80 rounded-xl">
                          <SelectGroup>
                            <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                              Home Services
                            </SelectLabel>
                            <SelectItem value="plumber">Plumber</SelectItem>
                            <SelectItem value="electrician">
                              Electrician
                            </SelectItem>
                            <SelectItem value="hvac">HVAC</SelectItem>
                            <SelectItem value="general-contractor">
                              General Contractor
                            </SelectItem>
                            <SelectItem value="roofer">Roofer</SelectItem>
                            <SelectItem value="painter">Painter</SelectItem>
                            <SelectItem value="landscaping">
                              Landscaping / Lawn Care
                            </SelectItem>
                            <SelectItem value="cleaning-services">
                              Cleaning Services
                            </SelectItem>
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                              Food & Hospitality
                            </SelectLabel>
                            <SelectItem value="restaurant">
                              Restaurant
                            </SelectItem>
                            <SelectItem value="cafe">
                              Cafe / Coffee Shop
                            </SelectItem>
                            <SelectItem value="bakery">Bakery</SelectItem>
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                              Health & Medical
                            </SelectLabel>
                            <SelectItem value="dentist">Dentist</SelectItem>
                            <SelectItem value="doctor">
                              Doctor / Clinic
                            </SelectItem>
                            <SelectItem value="chiropractor">
                              Chiropractor
                            </SelectItem>
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                              Personal Care & Wellness
                            </SelectLabel>
                            <SelectItem value="hair-salon">
                              Hair Salon
                            </SelectItem>
                            <SelectItem value="barber">Barber</SelectItem>
                            <SelectItem value="spa">Spa / Massage</SelectItem>
                            <SelectItem value="gym">Fitness / Gym</SelectItem>
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                              Automotive
                            </SelectLabel>
                            <SelectItem value="auto-repair">
                              Auto Repair
                            </SelectItem>
                            <SelectItem value="car-wash">Car Wash</SelectItem>
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                              Other
                            </SelectLabel>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </SelectPrimitive>
                      {errors?.industry && touched?.industry && (
                        <p className="text-red-500 text-sm">
                          {errors?.industry}
                        </p>
                      )}
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="timezone"
                        className="text-sm sm:text-base font-semibold flex items-center gap-2">
                        <Clock className="h-5 w-5 text-muted-foreground" />
                        Time Zone
                      </Label>
                      <p className="text-sm text-muted-foreground -mt-1">
                        Your business operating time zone
                      </p>
                      <Select
                        required
                        name="timeZone"
                        options={timeZones}
                        value={resolveTimeZoneOption(values?.timeZone)}
                        onChange={(selectedOption) =>
                          setFieldValue('timeZone', selectedOption?.label)
                        }
                        onBlur={() =>
                          setFieldValue('timeZone', values?.timeZone)
                        }
                        isDisabled={!hasProPlan}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          ...getBaseSelectStyles(
                            touched?.timeZone && errors?.timeZone
                          ),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                      />
                      {errors?.timeZone && touched?.timeZone && (
                        <p className="text-red-500 text-sm">
                          {errors?.timeZone}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label
                      htmlFor="address"
                      className="text-sm sm:text-base font-semibold flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-muted-foreground" />
                      Business Address
                    </Label>
                    <p className="text-sm text-muted-foreground -mt-1">
                      Your physical business location that customers can visit
                    </p>
                    <Input
                      id="mergeFields.~Address"
                      type="text"
                      onChange={handleChange}
                      value={values?.mergeFields['~Address'] || ''}
                      placeholder="Enter ~Address"
                      onBlur={handleBlur}
                      disabled={!hasProPlan}
                      className="h-12 sm:h-14 text-base sm:text-lg rounded-xl border-2 focus:border-primary px-4"
                    />
                  </div>

                  {/* Service Area - Integrated */}
                  <div className="space-y-3">
                    <Label className="text-sm sm:text-base font-semibold flex items-center gap-2">
                      <MapPinned className="h-5 w-5 text-muted-foreground" />
                      Service Area
                    </Label>
                    <p className="text-sm text-muted-foreground -mt-1">
                      Which cities or areas do you serve?
                    </p>
                    <Select
                      id="serviceArea"
                      name="serviceArea"
                      isMulti
                      isClearable={false}
                      isSearchable
                      inputValue={serviceAreaInput}
                      value={values?.serviceArea?.map((servA, index) => ({
                        value: servA,
                        label: servA
                      }))}
                      options={serviceAreaOptions}
                      isLoading={serviceAreaLoading}
                      noOptionsMessage={() =>
                        serviceAreaInput.trim().length < 3
                          ? 'Type at least 3 characters'
                          : 'No matching US location found'
                      }
                      onInputChange={(inputValue, actionMeta) => {
                        if (actionMeta.action === 'input-change') {
                          setServiceAreaInput(inputValue);
                        }
                        return inputValue;
                      }}
                      onChange={(selectedOptions) => {
                        setFieldValue(
                          'serviceArea',
                          selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : []
                        );
                        setServiceAreaInput('');
                        clearServiceAreaPredictions();
                      }}
                      placeholder="Type a service area"
                      onBlur={() => setFieldTouched('serviceArea', true, true)}
                      isDisabled={!hasProPlan}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        ...customMultiSelectorStyle,
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (provided, state) => ({
                          ...customMultiSelectorStyle.control(provided, state),
                          borderColor:
                            touched?.serviceArea && errors?.serviceArea
                              ? '#ef4444'
                              : '#e2e8f0',
                          boxShadow:
                            touched?.serviceArea && errors?.serviceArea
                              ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                              : 'none',
                          '&:hover': {
                            borderColor:
                              touched?.serviceArea && errors?.serviceArea
                                ? '#ef4444'
                                : '#3B5998'
                          }
                        })
                      }}
                    />
                    {touched?.serviceArea && errors?.serviceArea && (
                      <p className="text-red-500 text-sm">
                        {errors?.serviceArea}
                      </p>
                    )}
                  </div>
                  {/* Website */}
                  <div className="space-y-3">
                    <Label
                      htmlFor="website"
                      className="text-sm sm:text-base font-semibold flex items-center gap-2">
                      <Globe className="h-5 w-5 text-muted-foreground" />
                      Website
                    </Label>
                    <p className="text-sm text-muted-foreground -mt-1">
                      Your business website URL where customers can learn more
                    </p>
                    <Input
                      id="mergeFields.~Website"
                      type="text"
                      onChange={handleChange}
                      value={values?.mergeFields['~Website'] || ''}
                      className={`h-12 sm:h-14 text-base sm:text-lg rounded-xl border-2 px-4 ${
                        touched?.mergeFields?.['~Website'] &&
                        errors?.mergeFields?.['~Website']
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-border focus:border-primary'
                      }`}
                      placeholder="Enter Website"
                      onBlur={handleBlur}
                      disabled={!hasProPlan}
                    />
                    {touched?.mergeFields?.['~Website'] &&
                      errors?.mergeFields?.['~Website'] && (
                        <p className="text-red-500 text-sm">
                          {errors?.mergeFields?.['~Website']}
                        </p>
                      )}
                  </div>

                  {/* Description */}
                  <div className="space-y-3">
                    <Label
                      htmlFor="description"
                      className="text-base font-semibold">
                      Business Description
                    </Label>
                    <p className="text-sm text-muted-foreground -mt-1">
                      A short bio that appears on your Google listing and
                      directories
                    </p>
                    <Textarea
                      id="description"
                      onChange={handleChange}
                      value={values?.description}
                      onBlur={handleBlur}
                      rows={3}
                      placeholder="Describe your business..."
                      disabled={!hasProPlan}
                      className="min-h-[120px] sm:min-h-[140px] text-base sm:text-lg rounded-xl border-2 focus:border-primary p-3 sm:p-4 resize-none"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-md rounded-xl sm:rounded-2xl border-0 overflow-hidden">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="h-9 w-9 sm:h-12 sm:w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <Clock className="h-4 w-4 sm:h-6 sm:w-6 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base sm:text-xl lg:text-2xl font-bold break-words">
                        Business Hours
                      </CardTitle>
                      <CardDescription className="sm:block text-sm sm:text-base mt-1">
                        When are you open for business?
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  <BusinessHours
                    values={values}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    touched={touched}
                    hasProPlan={hasProPlan}
                  />
                </CardContent>
              </Card>

              <Card className="shadow-md rounded-xl sm:rounded-2xl border-0 overflow-hidden">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="h-9 w-9 sm:h-12 sm:w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <Briefcase className="h-4 w-4 sm:h-6 sm:w-6 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base sm:text-xl lg:text-2xl font-bold break-words">
                        Services Offered
                      </CardTitle>
                      <CardDescription className="sm:block text-sm sm:text-base mt-1">
                        What services does your business provide?
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  <CreatableSelect
                    id="primaryService"
                    name="primaryService"
                    isMulti
                    isClearable={false}
                    value={values?.primaryService?.map((servA, index) => ({
                      value: index,
                      label: servA
                    }))}
                    noOptionsMessage={() => 'Type a primary service'}
                    onChange={(selectedOption) => {
                      setFieldValue(
                        'primaryService',
                        selectedOption
                          ? selectedOption.map((servA) => servA.label)
                          : []
                      );
                    }}
                    onBlur={() => setFieldTouched('primaryService', true, true)}
                    options={[]}
                    placeholder="Type a primary service"
                    isDisabled={!hasProPlan}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      ...customMultiSelectorStyle,
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided, state) => ({
                        ...customMultiSelectorStyle.control(provided, state),
                        borderColor:
                          touched?.primaryService && errors?.primaryService
                            ? '#ef4444'
                            : state?.isFocused
                            ? PRIMARY_COLOR
                            : BORDER_COLOR,
                        boxShadow:
                          touched?.primaryService && errors?.primaryService
                            ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                            : 'none'
                      }),
                      multiValue: (provided, state) => {
                        const valueLabel = state?.data?.label;
                        const isInvalid =
                          isPrimaryServiceValueInvalid(valueLabel);

                        if (!isInvalid) {
                          return customMultiSelectorStyle.multiValue(provided);
                        }

                        return {
                          ...customMultiSelectorStyle.multiValue(provided),
                          backgroundColor: '#fee2e2',
                          border: '1px solid #ef4444'
                        };
                      }
                    }}
                  />
                  {touched?.primaryService && errors?.primaryService && (
                    <p className="text-red-500 text-sm mt-2">
                      {getFirstErrorMessage(errors?.primaryService) ||
                        'Primary Service contains unsupported characters. Remove symbols like ® or ™.'}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Client Content Preference */}
              <Card className="shadow-md rounded-2xl border-0">
                <CardContent className="px-4 sm:px-6 lg:px-8 py-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-base sm:text-lg font-semibold text-foreground">
                        Client doesn't want content
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        When enabled, the calendar will show a "No Automated
                        Content" tag
                      </p>
                    </div>
                    <Switch
                      checked={Boolean(values?.noAutomaticContent)}
                      onCheckedChange={(checked) =>
                        setFieldValue('noAutomaticContent', checked)
                      }
                      disabled={!hasProPlan}
                      className="data-[state=checked]:bg-primary shrink-0"
                    />
                  </div>
                </CardContent>
              </Card>

              {hasProPlan && (
                <div className="pt-2 pb-4 sm:pt-0 sm:pb-0 flex justify-end w-full">
                  <Button
                    size="lg"
                    type="button"
                    disabled={loading || uploadingLogo}
                    onClick={handleSaveClick}
                    style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 sm:h-14 w-full sm:w-auto px-6 sm:px-8 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3">
                    <Save className="h-5 w-5" />
                    {uploadingLogo ? (
                      'Uploading Logo...'
                    ) : loading ? (
                      <Spinner className="h-5 w-5" />
                    ) : (
                      'Save Profile'
                    )}
                  </Button>
                </div>
              )}
            </Form>

            {/* Success Modal */}
            <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
              <DialogContent className="sm:max-w-md text-center py-10">
                <DialogHeader className="items-center">
                  <DialogTitle className="text-2xl font-bold text-foreground">
                    Success!
                  </DialogTitle>
                  <DialogDescription className="text-base text-muted-foreground mt-2">
                    The client was updated successfully.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-center mt-4">
                  <Button
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                    onClick={() => setSuccessModalOpen(false)}>
                    OK
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Error Modal */}
            <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
              <DialogContent className="sm:max-w-md text-center py-10">
                <DialogHeader className="items-center">
                  <DialogTitle className="text-2xl font-bold text-foreground">
                    Error!
                  </DialogTitle>
                  <DialogDescription className="text-base text-muted-foreground mt-2">
                    {errorDetail || "We can't edit the client due to an error."}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-center mt-4">
                  <Button
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                    onClick={() => setErrorModalOpen(false)}>
                    OK
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Footer */}
          </>
        );
      }}
    </Formik>
  );
};

export default UnifiedAboutContent;
